import {
  FilterCustomizationDataDto,
  FilterCustomizationDataDtoResponse,
  KSendSuccessResponse,
} from '@asset-metrix/portal-gateway-client-js';
import { QueryClient, QueryFunction } from '@tanstack/react-query';

import { HTTP_STATUS } from '../../utils/constants';
import { HttpError } from '../errors';
import { portalGatewayClient } from '../portalGatewayClient';

export type ReferenceType = 'content' | 'page';

export function filterCustomizationQueryKey(
  referenceType: ReferenceType,
  reference: string
) {
  return ['filterCustomization', { referenceType, reference }] as const;
}

export function setFilterCustomizationQueryData(
  queryClient: QueryClient,
  referenceType: ReferenceType,
  reference: string,
  data: FilterCustomizationDataDto
): void {
  queryClient.setQueryData<FilterCustomizationDataDtoResponse>(
    filterCustomizationQueryKey(referenceType, reference),
    { status: 'success', data }
  );
}

/** Draft column ids win when present, including an empty list. */
export function activeCustomFilterColumnIds(
  draftColumnIds: readonly string[] | undefined,
  storedColumnIds: readonly string[] | undefined,
  knownColumnIds: readonly string[]
): string[] {
  const columnIds = draftColumnIds ?? storedColumnIds ?? [];
  return columnIds.filter((columnId) => knownColumnIds.includes(columnId));
}

const encodeReference = (reference: string): string =>
  encodeURIComponent(reference);

type FetchFilterCustomizationQueryKey = ReturnType<
  typeof filterCustomizationQueryKey
>;

export const fetchFilterCustomization: QueryFunction<
  FilterCustomizationDataDtoResponse | null,
  FetchFilterCustomizationQueryKey
> = async ({ queryKey }) => {
  const [, { referenceType, reference }] = queryKey;
  const result = await portalGatewayClient.table.getFilterCustomization(
    referenceType,
    encodeReference(reference)
  );

  return result.data;
};

export const mutateFilterCustomization = async ({
  referenceType,
  reference,
  data,
}: {
  referenceType: ReferenceType;
  reference: string;
  data: FilterCustomizationDataDto;
}): Promise<KSendSuccessResponse> => {
  const response = await portalGatewayClient.table.putFilterCustomization(
    referenceType,
    encodeReference(reference),
    data
  );

  if (!response.ok) {
    throw new HttpError('Saving filter customization failed.', response);
  }

  return response.data;
};

export const deleteFilterCustomization = async ({
  referenceType,
  reference,
}: {
  referenceType: ReferenceType;
  reference: string;
}): Promise<KSendSuccessResponse> => {
  const response = await portalGatewayClient.table.deleteFilterCustomization(
    referenceType,
    encodeReference(reference)
  );

  if (!response.ok && response.status !== HTTP_STATUS.NOT_FOUND) {
    throw new HttpError('Deleting filter customization failed.', response);
  }

  return response.data;
};
